import { useState } from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ArrowLeft, CreditCard, FileText, History, Edit, MoreHorizontal, Trash2 } from 'lucide-react';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrder, useUpdateOrderStatus } from '@/hooks/use-orders';
import { useJournal, useJournalByOrder } from '@/hooks/use-journals';
import { useCustomer } from '@/hooks/use-customers';
import { JournalDetailDialog } from '@/components/journals/journal-detail-dialog';
import { OrderHistoryDialog } from '@/components/orders/order-history-dialog';
import { CreatePaymentDialog } from '@/components/orders/create-payment-dialog';
import { EditOrderDialog } from '@/components/orders/edit-order-dialog';
import { ReceiptPrinter } from '@/components/orders/receipt-printer';
import { OrderTimeline } from '@/components/orders/order-timeline';
import { PageHeader } from '@/components/page-header';
import { useToast } from '@/hooks/use-toast';
import { Loader2, RefreshCcw } from 'lucide-react';

export const Route = createFileRoute('/orders/$id')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <OrderDetailPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
}

function safeFormatDate(dateString: string) {
  try {
    return format(new Date(dateString), 'dd MMMM yyyy, HH:mm');
  } catch {
    return '-';
  }
}

function OrderDetailPage() {
  const { id } = useParams({ from: '/orders/$id' });
  const { toast } = useToast();
  const { data: order, isLoading, error } = useOrder(id as string);
  const updateStatus = useUpdateOrderStatus();
  const [isJournalOpen, setIsJournalOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  
  // Fetch customer separately if not included in order
  const { data: customerDetails } = useCustomer(
    order?.customerId && order?.customer?.name === 'Unknown' ? order.customerId : ''
  );
  
  // Use customer from order or fetched customer
  const customer = order?.customer?.name !== 'Unknown' ? order?.customer : customerDetails;
  
  const { data: journalById } = useJournal(order?.journalEntryId || '');
  const { data: journalByOrder } = useJournalByOrder(order?.id || '');
  
  const journal = journalById || journalByOrder;

  const handleStatusChange = async (status: string) => {
    setUpdatingId('ORDER');
    try {
      await updateStatus.mutateAsync({ 
        orderId: id as string, 
        status 
      });
      toast({
        title: 'Order Status Updated',
        description: `Order has been moved to ${status}.`,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update order status',
        variant: 'destructive',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center py-24 text-destructive">
        <p>Failed to load order details.</p>
        <Link to="/orders">
          <Button variant="outline" className="mt-4">Back to Orders</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full min-w-0">
      {/* Header */}
      <PageHeader
        title={`Order ${order.orderNumber}`}
        description={safeFormatDate(order.createdAt)}
        actions={(
          <>
            <Link to="/orders">
              <Button variant="outline" size="sm" className="hidden sm:inline-flex">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </Link>
            
            {order.paymentStatus !== 'PAID' && (
              <Button onClick={() => setIsPaymentOpen(true)} size="sm">
                <CreditCard className="mr-2 h-4 w-4" />
                Pay
              </Button>
            )}

            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsEditOpen(true)}
              disabled={order.orderStatus === 'CANCELLED' || order.orderStatus === 'DELIVERED'}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>

            {/* Actions (Visible on all sizes, text hidden on mobile) */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsJournalOpen(true)}
                className="font-semibold"
                title="Journal"
              >
                <FileText className="h-4 w-4 text-blue-600 md:mr-2" />
                <span className="hidden md:inline">Journal</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsHistoryOpen(true)}
                className="font-semibold"
                title="History"
              >
                <History className="h-4 w-4 text-amber-600 md:mr-2" />
                <span className="hidden md:inline">History</span>
              </Button>

              <ReceiptPrinter
                orderId={id as string}
                orderNumber={order.orderNumber}
              />

              <Button 
                variant="outline" 
                size="sm" 
                className="text-destructive hover:bg-destructive hover:text-destructive-foreground font-semibold"
                title="Delete"
              >
                <Trash2 className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Delete</span>
              </Button>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="px-2">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {order.paymentStatus !== 'PAID' && (
                  <DropdownMenuItem onClick={() => setIsEditOpen(true)}>
                    <Edit className="mr-2 h-4 w-4" /> Edit Order
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-3 flex flex-col justify-center rounded-xl">
          <div className="space-y-0.5">
            <p className="text-xs text-muted-foreground">Customer</p>
            <p className="text-base font-semibold">
              {customer?.name || order.customer.name}
              {order?.customer?.name === 'Unknown' && !customerDetails && (
                <Loader2 className="inline-block ml-2 h-3 w-3 animate-spin" />
              )}
            </p>
            {(customer?.phone || order.customer.phone) && (
              <p className="text-xs text-muted-foreground">
                {customer?.phone || order.customer.phone}
              </p>
            )}
          </div>
        </Card>
        <Card className="p-3 flex flex-col justify-center rounded-xl">
          <div className="space-y-0.5">
            <p className="text-xs text-muted-foreground">Created By</p>
            <p className="text-base font-semibold">{order.createdBy?.name || '-'}</p>
            <p className="text-xs text-muted-foreground">
              {safeFormatDate(order.createdAt)}
            </p>
          </div>
        </Card>
        <Card className="p-3 flex flex-col justify-center rounded-xl">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Payment Status</p>
            <Badge
              variant={
                order.paymentStatus === 'PAID'
                  ? 'success'
                  : order.paymentStatus === 'PARTIAL'
                    ? 'secondary'
                    : 'destructive'
              }
              className="text-xs"
            >
              {order.paymentStatus}
            </Badge>
          </div>
        </Card>
        <Card className="p-3 flex flex-col justify-center rounded-xl">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground mb-1">Order Status</p>
            <div className="flex items-center gap-2">
              <Select
                value={order.orderStatus === 'READY' ? 'READY' : order.orderStatus}
                onValueChange={handleStatusChange}
                disabled={updatingId === 'ORDER' || order.orderStatus === 'CANCELLED' || order.orderStatus === 'DELIVERED'}
              >
                <SelectTrigger className="h-8 text-xs font-semibold bg-primary/5 border-primary/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="RECEIVED">Received</SelectItem>
                  <SelectItem value="WASHING">Washing</SelectItem>
                  <SelectItem value="DRYING">Drying</SelectItem>
                  <SelectItem value="IRONING">Ironing</SelectItem>
                  <SelectItem value="PACKING">Packing</SelectItem>
                  <SelectItem value="READY">Ready</SelectItem>
                  <SelectItem value="DELIVERED">Delivered</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              {updatingId === 'ORDER' && (
                <RefreshCcw className="h-3 w-3 animate-spin text-primary" />
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Order Items */}
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle className="text-lg">Order Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 md:hidden">
            {order.items.map((item) => (
              <article key={item.id} className="rounded-xl border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{item.service.name}</p>
                    <p className="text-xs text-muted-foreground">{item.service.unit}</p>
                  </div>
                  <p className="text-sm font-semibold">{formatCurrency(item.subtotal)}</p>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-md bg-muted/40 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Qty</p>
                    <p className="font-semibold">{item.quantity}</p>
                  </div>
                  <div className="rounded-md bg-muted/40 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Unit Price</p>
                    <p className="font-semibold">{formatCurrency(item.unitPrice)}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                   <Badge variant="secondary" className="w-full justify-center">
                      STATUS: {order.orderStatus}
                   </Badge>
                </div>
              </article>
            ))}
            <div className="rounded-xl border bg-muted/30 px-4 py-3 text-sm font-semibold">
              <div className="flex items-center justify-between">
                <span>Total</span>
                <span>{formatCurrency(order.totalAmount)}</span>
              </div>
            </div>
          </div>

          <div className="hidden overflow-x-auto md:block">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px] hidden md:table-cell">No</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Unit</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right hidden md:table-cell">Unit Price</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.items.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell className="text-muted-foreground text-xs hidden md:table-cell">
                    {index + 1}
                  </TableCell>
                  <TableCell className="font-medium truncate max-w-[120px]">{item.service.name}</TableCell>
                  <TableCell>
                    <Badge variant={order.orderStatus === 'READY' || order.orderStatus === 'DELIVERED' ? 'success' : 'secondary'} className="text-[10px]">
                      {order.orderStatus === 'READY' || order.orderStatus === 'DELIVERED' ? 'DONE' : order.orderStatus}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{item.service.unit}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-right hidden md:table-cell">
                    {formatCurrency(item.unitPrice)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(item.subtotal)}
                  </TableCell>
                </TableRow>
              ))}
                {/* Desktop Total Row */}
                <TableRow className="hidden md:table-row">
                  <TableCell colSpan={6} className="text-right font-bold pt-4">
                    Total
                  </TableCell>
                  <TableCell className="text-right font-bold text-lg pt-4">
                    {formatCurrency(order.totalAmount)}
                  </TableCell>
                </TableRow>
                {/* Mobile Total Row */}
                <TableRow className="md:hidden">
                  <TableCell colSpan={3} className="text-right font-bold pt-4">
                    Total
                  </TableCell>
                  <TableCell className="text-right font-bold text-lg pt-4">
                    {formatCurrency(order.totalAmount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Production Timeline */}
      <OrderTimeline invoiceId={id as string} />

      {/* Notes */}
      {order.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{order.notes}</p>
          </CardContent>
        </Card>
      )}
      <JournalDetailDialog 
        open={isJournalOpen} 
        onOpenChange={setIsJournalOpen} 
        journal={journal || null} 
      />
      
      <OrderHistoryDialog 
        open={isHistoryOpen} 
        onOpenChange={setIsHistoryOpen} 
        orderId={id as string} 
      />
      
      {order && (
        <CreatePaymentDialog 
          open={isPaymentOpen} 
          onOpenChange={setIsPaymentOpen} 
          order={order} 
        />
      )}
      
      {order && (
        <EditOrderDialog
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          order={order}
        />
      )}
    </div>
  );
}
